/**
 * Spectator Multiplex — grid of every watchable game (Live Java per-player
 * taps + Neo tables), filterable by mode, with a grand-format replay view.
 */

import { useEffect, useMemo, useState } from 'react';
import type { Socket } from 'socket.io-client';
import TimedReplayView from '../components/TimedReplayView';
import SpectatorThumbnail from '../components/SpectatorThumbnail';
import { useSpectatorPlayback, useSpectatorSources } from '../hooks/useSpectatorPlayback';
import type { SpectatorMode, SpectatorLiveProgress, WatchableSourceSummary } from '../hooks/useSpectatorPlayback';
import '../styles/spectator.css';

interface SpectatorMultiplexProps {
  socket: Socket | null;
  pseudo: string;
  hasCompetitionAccess?: boolean;
  onBack?: () => void;
  /** Opens the multiplex with a filter already active (e.g. from the Live category button). */
  initialFilter?: SpectatorMode | 'all';
}

const FILTERS: Array<{ id: SpectatorMode | 'all'; label: string }> = [
  { id: 'live', label: 'Live' },
  { id: 'neo_libre', label: 'Neo libre' },
  { id: 'all', label: 'Tous' },
];

/** Sorting rules for the Live mosaic — see MODE_SPECTATEUR.md. Only meaningful when watching Live sources. */
type SortMode = 'pseudo_asc' | 'progress_desc' | 'taker_perf' | 'last_deal_result' | 'tournament_rank';

const SORT_OPTIONS: Array<{ id: SortMode; label: string }> = [
  { id: 'pseudo_asc', label: 'Pseudo (A→Z)' },
  { id: 'progress_desc', label: 'Avancement de la donne' },
  { id: 'taker_perf', label: 'Performance en cours' },
  { id: 'last_deal_result', label: 'Résultat dernière donne' },
  { id: 'tournament_rank', label: 'Classement du tournoi' },
];

/** Higher = better for the observed human player. Unknown data sorts last. */
function performanceScore(progress?: SpectatorLiveProgress): number {
  if (!progress || progress.takerPoints === undefined || progress.observedIsTaker === undefined) {
    return Number.NEGATIVE_INFINITY;
  }
  return progress.observedIsTaker ? progress.takerPoints : -progress.takerPoints;
}

function sortSources(list: WatchableSourceSummary[], sortMode: SortMode): WatchableSourceSummary[] {
  const arr = [...list];
  switch (sortMode) {
    case 'pseudo_asc':
      arr.sort((a, b) => (a.playerPseudo ?? a.tableLabel).localeCompare(b.playerPseudo ?? b.tableLabel, 'fr'));
      break;
    case 'progress_desc':
      arr.sort((a, b) => (b.liveProgress?.trickNumber ?? -1) - (a.liveProgress?.trickNumber ?? -1));
      break;
    case 'taker_perf':
      arr.sort((a, b) => performanceScore(b.liveProgress) - performanceScore(a.liveProgress));
      break;
    case 'last_deal_result':
      arr.sort((a, b) => performanceScore(b.lastDealResult) - performanceScore(a.lastDealResult));
      break;
    case 'tournament_rank':
      arr.sort((a, b) => {
        const ra = a.liveRanking?.clTournoi ?? Number.MAX_SAFE_INTEGER;
        const rb = b.liveRanking?.clTournoi ?? Number.MAX_SAFE_INTEGER;
        return ra - rb;
      });
      break;
    default:
      break;
  }
  return arr;
}

/** Table de sourceId -> rang (1/2/3) parmi les joueurs les mieux classés au tournoi Live en cours. */
function computeTop3(list: WatchableSourceSummary[]): Map<string, 1 | 2 | 3> {
  const ranked = list
    .filter(s => s.mode === 'live' && s.liveRanking?.clTournoi !== undefined)
    .sort((a, b) => (a.liveRanking!.clTournoi! - b.liveRanking!.clTournoi!))
    .slice(0, 3);
  const map = new Map<string, 1 | 2 | 3>();
  ranked.forEach((s, idx) => map.set(s.sourceId, (idx + 1) as 1 | 2 | 3));
  return map;
}

export default function SpectatorMultiplex({
  socket,
  pseudo,
  hasCompetitionAccess,
  onBack,
  initialFilter = 'live',
}: SpectatorMultiplexProps) {
  const [filter, setFilter] = useState<SpectatorMode | 'all'>(initialFilter);
  const [sortMode, setSortMode] = useState<SortMode>('pseudo_asc');
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);

  const { sources: rawSources, loading } = useSpectatorSources(socket, filter, pseudo);

  const sources = useMemo(() => sortSources(rawSources, sortMode), [rawSources, sortMode]);
  const top3BySourceId = useMemo(() => computeTop3(rawSources), [rawSources]);

  const {
    playbackState,
    events,
    chatMessages,
    join,
    leave,
  } = useSpectatorPlayback(socket, selectedSourceId);

  useEffect(() => {
    setFilter(initialFilter);
  }, [initialFilter]);

  useEffect(() => {
    if (selectedSourceId && socket) {
      join({ pseudo, hasCompetitionAccess });
    }
    return () => {
      if (selectedSourceId) leave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSourceId, socket]);

  // Si la source sélectionnée disparaît de la liste (déconnexion), revenir à la mosaïque.
  useEffect(() => {
    if (selectedSourceId && !sources.some(s => s.sourceId === selectedSourceId)) {
      setSelectedSourceId(null);
    }
  }, [sources, selectedSourceId]);

  const handleSelect = (sourceId: string) => {
    if (selectedSourceId && selectedSourceId !== sourceId) leave();
    setSelectedSourceId(sourceId);
  };

  const handleBackToMosaic = () => {
    leave();
    setSelectedSourceId(null);
  };

  const selectedSource = sources.find(s => s.sourceId === selectedSourceId);

  const filterBar = (
    <div className="spectator-filter-bar">
      {FILTERS.map(f => (
        <button
          key={f.id}
          type="button"
          className={`spectator-filter-btn ${filter === f.id ? 'active' : ''}`}
          onClick={() => setFilter(f.id)}
        >
          {f.label}
        </button>
      ))}
      {filter === 'live' && (
        <label className="spectator-sort-select">
          Trier par
          <select value={sortMode} onChange={e => setSortMode(e.target.value as SortMode)}>
            {SORT_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </label>
      )}
    </div>
  );

  if (loading && sources.length === 0) {
    return (
      <div className="live-spectator-page">
        <p>Chargement des parties…</p>
      </div>
    );
  }

  if (selectedSourceId && selectedSource) {
    return (
      <div className="live-spectator-page">
        <div className="live-spectator-header">
          <button type="button" className="live-spectator-back" onClick={handleBackToMosaic}>
            ← Mosaïque
          </button>
          <h2>{selectedSource.tableLabel}</h2>
          {selectedSource.liveDonnes && (
            <span>
              Donne {selectedSource.liveDonnes.current}/{selectedSource.liveDonnes.max}
            </span>
          )}
        </div>

        {sources.length > 1 && (
          <div className="live-spectator-mosaic live-spectator-mosaic-strip">
            {sources.map(src => (
              <button
                key={src.sourceId}
                type="button"
                className={`live-spectator-tile ${src.sourceId === selectedSourceId ? 'selected' : ''}`}
                onClick={() => handleSelect(src.sourceId)}
              >
                {src.tableLabel}
              </button>
            ))}
          </div>
        )}

        <div className="live-spectator-main">
          <TimedReplayView
            events={events}
            chatMessages={chatMessages}
            players={playbackState?.players ?? selectedSource.players}
            waitingMessage={playbackState?.message}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="live-spectator-page">
      <div className="live-spectator-header">
        <h2>Spectateur — Multiplex</h2>
        {onBack && (
          <button type="button" onClick={onBack}>Retour aux tables</button>
        )}
      </div>

      {filterBar}

      {sources.length === 0 ? (
        <p className="spectator-waiting">
          Aucune partie regardable pour le moment.
        </p>
      ) : (
        <div className="spectator-thumbnail-grid">
          {sources.map(src => (
            <SpectatorThumbnail
              key={src.sourceId}
              source={src}
              selected={src.sourceId === selectedSourceId}
              highlightRank={top3BySourceId.get(src.sourceId)}
              onClick={() => handleSelect(src.sourceId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
